/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";

/**
 * Keyframes for the gradient animation
 */
const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

/**
 * Styled text with animated gradient
 */
const StyledText = styled(motion.span)(() => ({
  fontSize: "0.9rem",
  display: "inline-block",
  background: "linear-gradient(90deg, rgb(82, 145, 197), rgb(217, 160, 250), rgb(179, 167, 255), rgb(137, 139, 221), #74b9ff, #a29bfe, #fd79a8)",
  backgroundSize: "300% 300%",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  animation: `${gradientAnimation} 3s infinite ease-in-out`,
}));

export default function AnimatedText({ value }) {
  const [displayText, setDisplayText] = useState(value);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (isMounted) {
      setDisplayText(value);
    } else {
      setIsMounted(true);
    }
  }, [value]);

  return (
    <div className="relative inline-block">
      <AnimatePresence mode="wait">
        <StyledText
          key={displayText} // Triggers animation on change
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease:"easeIn" }}
          className="absolute"
        >
          {displayText}
        </StyledText>
      </AnimatePresence>
    </div>
  );
}
